import React, { useState, useEffect, useReducer } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import CountUpNumber from './CountUpNumber';
import EffectBreakdownTooltip from './EffectBreakdownTooltip'; // [PHASE 2.3 - NOV 15, 2025]: Effect breakdown tooltips
import { systemBus, EVENTS } from '../services/systemBus';
import { effectCalculator } from '../services/effectCalculator';
import { effectRegistry } from '../services/effectRegistry'; // [T5 — AC-bug fix 2026-05-31]
import { useCharacter } from '../contexts/CharacterContext';
import { useInventory } from '../contexts/InventoryContext';
import { isSpellcaster as checkIsSpellcaster } from '../utils/contentResolver';
import { computeConditionConsequences } from '../utils/conditionConsequences';
import UsableAbilityModal from './UsableAbilityModal'; // [1H-G-bw-S3a]: reaction resolution modal
import { computeAbilitySaveDc } from '../utils/usableAbilityFormulas'; // [1H-G-bw-S3a]: shared save-DC formula (Rule 22)

const StatsPanel = ({
  // [PHASE 4B - PARTIAL MIGRATION]: Keeping function/calculated props for now
  // [WHY]: These will be moved in Phase 4B Step 6 (Function Migration)
  totalWeight,
  encumbranceLevel,
  getAvailableSpells,
  resetDailySpells
}) => {
  // [PHASE 4B - MIGRATED]: Now using context hooks for state
  const { playerStats, setPlayerStats, temporaryStats, logActivity, activeQuickBonuses } = useCharacter();
  const { inventory } = useInventory();
  const [expandedSections, setExpandedSections] = useState({
    abilities: true,
    spells: false,
    itemSpells: false,
    acBonuses: false
  });

  // [INTEGRATION]: Force re-render mechanism for reactive updates
  // [WHY]: When events fire, component needs to re-render with new stat calculations
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // [Sub-plan 1H-G-bw-S3a — 2026-06-03]: Reaction resolution modal state. A
  // reaction carrying a rollSpec (e.g. Hellish Rebuke) opens this on Invoke; the
  // pool decrement + activity log happen on Apply. Shape: { reaction, rollSpec } | null.
  const [reactionModal, setReactionModal] = useState(null);

  /**
   * Event Subscribers for Reactive Updates
   *
   * [INTEGRATION]: Subscribe to stat-affecting events from SystemBus
   * [WHY]: Stats displayed here are affected by feats, equipment, and other changes
   * [EVENTS SUBSCRIBED]:
   * - FEAT_ADDED / FEAT_REMOVED: Feats modify ability scores (e.g., Actor = +1 CHA)
   * - ITEM_EQUIPPED / ITEM_UNEQUIPPED: Items modify stats (e.g., Belt of Giant Strength)
   * - FEAT_TOGGLED: Toggle effects may affect stats
   *
   * Updated: December 10, 2024 - Added reactive event subscriptions
   */
  useEffect(() => {
    // [HELPER]: Central handler that clears cache and forces re-render
    const handleStatChange = (eventName, data) => {
      // [CRITICAL]: Clear cache BEFORE forcing update to get fresh calculations
      effectCalculator.clearCache();
      forceUpdate();
    };

    // Subscribe to feat events (affect ability scores via bonuses)
    const unsubscribeFeatAdded = systemBus.on(EVENTS.FEAT_ADDED, (data) =>
      handleStatChange('FEAT_ADDED', data));
    const unsubscribeFeatRemoved = systemBus.on(EVENTS.FEAT_REMOVED, (data) =>
      handleStatChange('FEAT_REMOVED', data));
    const unsubscribeFeatToggled = systemBus.on(EVENTS.FEAT_TOGGLED, (data) =>
      handleStatChange('FEAT_TOGGLED', data));

    // Subscribe to equipment events (items can SET/ADD to ability scores)
    const unsubscribeItemEquipped = systemBus.on(EVENTS.ITEM_EQUIPPED, (data) =>
      handleStatChange('ITEM_EQUIPPED', data));
    const unsubscribeItemUnequipped = systemBus.on(EVENTS.ITEM_UNEQUIPPED, (data) =>
      handleStatChange('ITEM_UNEQUIPPED', data));
    const unsubscribeItemAttuned = systemBus.on(EVENTS.ITEM_ATTUNED, (data) =>
      handleStatChange('ITEM_ATTUNED', data));
    const unsubscribeEquipmentChanged = systemBus.on(EVENTS.EQUIPMENT_CHANGED, (data) =>
      handleStatChange('EQUIPMENT_CHANGED', data));

    // Direct ability score changes
    const unsubscribeAbilityChanged = systemBus.on(EVENTS.ABILITY_SCORE_CHANGED, (data) =>
      handleStatChange('ABILITY_SCORE_CHANGED', data));

    // Level-up can grant ASIs
    const unsubscribeLevelUp = systemBus.on(EVENTS.LEVEL_UP, (data) =>
      handleStatChange('LEVEL_UP', data));

    // Quick bonuses can affect ability checks
    const unsubscribeQuickBonusAdded = systemBus.on(EVENTS.QUICK_BONUS_ADDED, (data) =>
      handleStatChange('QUICK_BONUS_ADDED', data));
    const unsubscribeQuickBonusRemoved = systemBus.on(EVENTS.QUICK_BONUS_REMOVED, (data) =>
      handleStatChange('QUICK_BONUS_REMOVED', data));

    // Condition dismissal and registry changes must also refresh stat displays
    const unsubscribeEffectsChanged = systemBus.on(EVENTS.EFFECTS_CHANGED, (data) =>
      handleStatChange('EFFECTS_CHANGED', data));
    const unsubscribeConditionRemoved = systemBus.on(EVENTS.COMBAT_CONDITION_REMOVED, (data) =>
      handleStatChange('COMBAT_CONDITION_REMOVED', data));

    // Cleanup: Unsubscribe on component unmount
    return () => {
      unsubscribeFeatAdded();
      unsubscribeFeatRemoved();
      unsubscribeFeatToggled();
      unsubscribeItemEquipped();
      unsubscribeItemUnequipped();
      unsubscribeItemAttuned();
      unsubscribeEquipmentChanged();
      unsubscribeAbilityChanged();
      unsubscribeLevelUp();
      unsubscribeQuickBonusAdded();
      unsubscribeQuickBonusRemoved();
      unsubscribeEffectsChanged();
      unsubscribeConditionRemoved();
    };
  }, []); // Empty dependency array = subscribe once on mount

  // [FIX - NOV 11, 2025]: Replaced manual modifier calculation with effectCalculator.getEffectiveModifier
  // [WHY]: Was redundant - got effective score then manually calculated modifier
  // [INTEGRATION]: Now uses effectCalculator for BOTH score AND modifier
  const calculateModifier = (score, ability = null) => {
    if (ability && playerStats) {
      // Use effectCalculator to get modifier directly (includes all bonuses)
      return effectCalculator.getEffectiveModifier(
        playerStats,
        inventory,
        activeQuickBonuses,
        ability
      );
    }
    // [FIX - NOV 11, 2025]: Use effectCalculator for consistency
    // [WHY]: Even fallback should use centralized calculation service
    return effectCalculator.getAbilityModifier(score);
  };
  const formatModifier = (modifier) => modifier >= 0 ? `+${modifier}` : `${modifier}`;

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  /**
   * Update ability score manually
   *
   * [INTEGRATION]: Now emits STATS_CHANGED event and clears EffectCalculator cache
   * [WHY]: Manual stat changes need to trigger reactive updates in other components
   * [D&D 5E]: Ability scores must be between 1-30
   *
   * Updated: December 10, 2024 - Added SystemBus integration
   */
  // [REMOVED 2025-10-23]: updateAbilityScore function removed
  // Ability scores are now edited in VitalStatsCard component which has better UX

  const spellAttackBonus = () => {
    // [FIX phase6-E E-1]: Route through effectCalculator.calculateSpellAttackBonus
    // so registered attackBonus effects (Wand of the War Mage +1, custom feats
    // with attackBonus effects) are picked up. Was bypassing effectRegistry.
    return effectCalculator.calculateSpellAttackBonus(
      playerStats,
      inventory,
      activeQuickBonuses
    );
  };

  const spellSaveDC = () => {
    // [FIX phase6-E E-1]: Route through effectCalculator.calculateSpellDC so
    // registered spellDC effects (Rod of the Pact Keeper +1) are picked up.
    return effectCalculator.calculateSpellDC(
      playerStats,
      inventory,
      activeQuickBonuses
    );
  };

  return (
    <div className="bg-gray-800 p-4 rounded-lg">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-bold text-white">Character Details</h3>
        {/* Inspiration Indicator */}
        {playerStats.inspiration > 0 && (
          <div className="flex items-center bg-gradient-to-r from-amber-700 to-yellow-600 px-2 py-1 rounded border border-amber-500">
            <span className="text-xs font-bold text-yellow-200" title={`${playerStats.inspiration} Inspiration point${playerStats.inspiration > 1 ? 's' : ''}`}>
              ⭐ {playerStats.inspiration}
            </span>
          </div>
        )}
      </div>
      
      <div className="space-y-3">
        {/* [REMOVED 2025-10-23]: Ability Scores section removed to prevent duplication with VitalStatsCard */}
        {/* VitalStatsCard displays ability scores in a better grid layout with animations */}

        {/* Character Level & Proficiency */}
        <div>
          <label className="text-sm text-gray-300">Level</label>
          <div className="w-full p-2 bg-gray-700 text-white rounded">
            {(() => {
              // Total level is ALWAYS the sum of all class levels
              const totalLevel = (playerStats.classes || []).reduce((sum, cls) => sum + (cls.level || 0), 0) || playerStats.level || 1;
              return totalLevel;
            })()}
          </div>
          {playerStats.classes && playerStats.classes.length > 0 && (
            <div className="text-xs text-gray-400 mt-1">
              {playerStats.classes.map(c => `${c.className.charAt(0).toUpperCase() + c.className.slice(1)} ${c.level}`).join(' / ')}
            </div>
          )}
          <div className="text-xs text-gray-400 mt-1">
            {/* [FIX - NOV 11, 2025]: Use effectCalculator for proficiency bonus */}
            {/* [PHASE 2.3 - NOV 15, 2025]: Added effect breakdown tooltip */}
            Proficiency Bonus: <EffectBreakdownTooltip
              stat="proficiencyBonus"
              baseValue={0}
              character={playerStats}
              inventory={inventory}
              activeQuickBonuses={activeQuickBonuses}
            >
              <span className="text-white font-mono cursor-help">
                +{effectCalculator.getProficiencyBonus(playerStats)}
              </span>
            </EffectBreakdownTooltip>
          </div>
        </div>

        {/* Spellcasting - Only show for spellcasters */}
        {(() => {
          // [v1.0]: Use contentResolver isSpellcaster() — supports custom classes
          const isSpellcaster = (
            checkIsSpellcaster(playerStats.classes?.[0]?.className || '') ||
            (playerStats.classes && playerStats.classes.some(cls =>
              checkIsSpellcaster(cls.className)
            ))
          );

          if (!isSpellcaster) return null;

          return (
            <div>
              <button
                onClick={() => toggleSection('spells')}
                className="flex items-center w-full text-sm font-medium text-white mb-2"
              >
                {expandedSections.spells ? <ChevronDown className="mr-1 h-4 w-4" /> : <ChevronRight className="mr-1 h-4 w-4" />}
                Spellcasting
              </button>
              {expandedSections.spells && (
            <div className="space-y-2 ml-4">
              <div>
                <label className="text-sm text-gray-300">Spellcasting Ability</label>
                <select
                  value={playerStats.spellcastingAbility || 'intelligence'}
                  onChange={(e) => setPlayerStats(prev => ({ ...prev, spellcastingAbility: e.target.value }))}
                  className="w-full p-2 bg-gray-700 text-white rounded"
                >
                  {(() => {
                    // Map classes to their spellcasting abilities
                    const classAbilities = {
                      wizard: { ability: 'intelligence', label: 'Intelligence (Wizard)' },
                      cleric: { ability: 'wisdom', label: 'Wisdom (Cleric)' },
                      druid: { ability: 'wisdom', label: 'Wisdom (Druid)' },
                      ranger: { ability: 'wisdom', label: 'Wisdom (Ranger)' },
                      bard: { ability: 'charisma', label: 'Charisma (Bard)' },
                      paladin: { ability: 'charisma', label: 'Charisma (Paladin)' },
                      sorcerer: { ability: 'charisma', label: 'Charisma (Sorcerer)' },
                      warlock: { ability: 'charisma', label: 'Charisma (Warlock)' }
                    };
                    
                    // Get all spellcasting classes the character has
                    const characterClasses = [];
                    const pc = playerStats.classes?.[0]?.className || '';
                    if (pc && classAbilities[pc.toLowerCase()]) {
                      characterClasses.push(pc.toLowerCase());
                    }
                    if (playerStats.classes) {
                      playerStats.classes.forEach(cls => {
                        const className = cls.className?.toLowerCase();
                        if (className && classAbilities[className] && !characterClasses.includes(className)) {
                          characterClasses.push(className);
                        }
                      });
                    }
                    
                    // Create unique ability options based on character's classes
                    const uniqueAbilities = new Map();
                    characterClasses.forEach(cls => {
                      const classInfo = classAbilities[cls];
                      if (classInfo) {
                        if (!uniqueAbilities.has(classInfo.ability)) {
                          uniqueAbilities.set(classInfo.ability, []);
                        }
                        uniqueAbilities.get(classInfo.ability).push(cls.charAt(0).toUpperCase() + cls.slice(1));
                      }
                    });
                    
                    // Generate options
                    const options = [];
                    uniqueAbilities.forEach((classes, ability) => {
                      const abilityName = ability.charAt(0).toUpperCase() + ability.slice(1);
                      options.push(
                        <option key={ability} value={ability}>
                          {abilityName} ({classes.join(', ')})
                        </option>
                      );
                    });
                    
                    // If no spellcasting classes, show all options
                    if (options.length === 0) {
                      return (
                        <>
                          <option value="intelligence">Intelligence (Wizard)</option>
                          <option value="wisdom">Wisdom (Cleric, Druid, Ranger)</option>
                          <option value="charisma">Charisma (Bard, Paladin, Sorcerer, Warlock)</option>
                        </>
                      );
                    }
                    
                    return options;
                  })()}
                </select>
              </div>
              
              {playerStats.spellcastingAbility && (
                <div className="bg-gray-700 p-2 rounded">
                  <div className="text-xs text-gray-300 space-y-1">
                    {/* [PHASE 2.3 - NOV 15, 2025]: Added effect breakdown tooltips */}
                    <div className="flex justify-between">
                      <span>Spell Attack Bonus:</span>
                      <EffectBreakdownTooltip
                        stat="spellAttackBonus"
                        baseValue={0}
                        character={playerStats}
                        inventory={inventory}
                        activeQuickBonuses={activeQuickBonuses}
                      >
                        <span className="font-mono cursor-help">+{spellAttackBonus()}</span>
                      </EffectBreakdownTooltip>
                    </div>
                    <div className="flex justify-between">
                      <span>Spell Save DC:</span>
                      <EffectBreakdownTooltip
                        stat="spellSaveDC"
                        baseValue={0}
                        character={playerStats}
                        inventory={inventory}
                        activeQuickBonuses={activeQuickBonuses}
                      >
                        <span className="font-mono cursor-help">{spellSaveDC()}</span>
                      </EffectBreakdownTooltip>
                    </div>
                    <div className="text-xs text-gray-500 mt-2">
                      {(() => {
                        // [FIX - NOV 11, 2025]: Use effectCalculator for proficiency bonus
                        // [WHY]: Consistent with other proficiency calculations
                        const ability = playerStats.spellcastingAbility;
                        const baseScore = playerStats[ability] || 10;
                        const tempStat = temporaryStats[ability] || {};
                        let totalScore = tempStat.set !== undefined ? tempStat.set : baseScore + (tempStat.add || 0);
                        const profBonus = effectCalculator.getProficiencyBonus(playerStats);
                        return `${profBonus} (prof) + ${formatModifier(calculateModifier(totalScore))} (${ability.substring(0, 3)})`;
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
            </div>
          );
        })()}

        {/* Item Spells */}
        <div>
          <button 
            onClick={() => toggleSection('itemSpells')}
            className="flex items-center w-full text-sm font-medium text-white mb-2"
          >
            {expandedSections.itemSpells ? <ChevronDown className="mr-1 h-4 w-4" /> : <ChevronRight className="mr-1 h-4 w-4" />}
            Item Spells
          </button>
          {expandedSections.itemSpells && (
            <div className="space-y-2 ml-4">
              {getAvailableSpells && getAvailableSpells().length > 0 ? (
                <>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs text-gray-400">Available Spells:</span>
                    <button
                      onClick={resetDailySpells}
                      className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-xs rounded transition-colors"
                    >
                      Reset Daily
                    </button>
                  </div>
                  {getAvailableSpells().map((spell, index) => (
                    <div key={`${spell.itemId}-${spell.spellIndex}`} className="bg-gray-700 p-2 rounded">
                      <div className="flex justify-between items-center">
                        <div className="flex-1">
                          <div className="text-sm font-medium text-white">{spell.name}</div>
                          <div className="text-xs text-gray-400">
                            From: {spell.itemName} | Level: {spell.level}
                          </div>
                          <div className="text-xs text-gray-400">
                            Uses: {spell.currentUses}/{spell.usesPerDay}
                            {spell.dc && ` | DC: ${spell.dc}`}
                          </div>
                        </div>
                        <button
                          onClick={spell.castSpell}
                          disabled={spell.currentUses <= 0}
                          className="px-2 py-1 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-xs rounded transition-colors"
                        >
                          Cast
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="text-xs text-gray-400 italic">No magical items equipped with spells</div>
              )}
            </div>
          )}
        </div>

        {/* [Sub-plan 1Q-F-7d 2026-05-28]: Condition consequence warnings.
            Renders when an active condition causes save auto-fail or grants
            advantage to attackers. Data-driven: queries effectCalculator
            (which reads effectRegistry) — no hardcoded condition-name checks.
            System-agnostic: any RPG condition registered with
            { type:'save', operation:'autoFail' } or { type:'attacksAgainst', operation:'advantage' }
            will appear here automatically. */}
        {(() => {
          const SAVE_ABILITIES = [
            { key: 'strength', label: 'STR', full: 'Strength' },
            { key: 'dexterity', label: 'DEX', full: 'Dexterity' },
            { key: 'constitution', label: 'CON', full: 'Constitution' },
            { key: 'intelligence', label: 'INT', full: 'Intelligence' },
            { key: 'wisdom', label: 'WIS', full: 'Wisdom' },
            { key: 'charisma', label: 'CHA', full: 'Charisma' },
          ];
          // [Sub-plan 1Q-F-7d-followup 2026-05-28]: Replace effectRegistry-driven
          // hasAutoFailSave / hasAttacksAgainst / hasAutoCritWithin5ft calls with
          // inline read of playerStats.conditions + SRD_CONDITIONS lookup.
          // The effectRegistry is populated by CharacterContext useEffect AFTER
          // the first render, so the old queries returned false on initial paint.
          // computeConditionConsequences reads playerStats.conditions synchronously
          // — no timing race, banners visible on first paint.
          const consequences = computeConditionConsequences(playerStats);
          const autoFailAbilities = SAVE_ABILITIES.filter(
            a => consequences.autoFailAbilities.has(a.key)
          );
          // [Sub-plan 1Q-F-7b-ii-ui 2026-05-28]: Ability-check auto-fail + disadvantage sections.
          // Don't double-render disadvantage on abilities that already auto-fail.
          const autoFailCheckAbilities = SAVE_ABILITIES.filter(
            a => consequences.autoFailChecks.has(a.key)
          );
          const disadvantageCheckList = SAVE_ABILITIES.filter(
            a => consequences.disadvantageCheckAbilities.has(a.key) && !consequences.autoFailChecks.has(a.key)
          );
          const hasAdvAtk = consequences.hasAdvAtk;
          const hasAutoCrit = consequences.hasAutoCrit;

          if (autoFailAbilities.length === 0 && autoFailCheckAbilities.length === 0 && disadvantageCheckList.length === 0 && !hasAdvAtk && !hasAutoCrit) return null;

          // Collect source condition names for tooltip
          const activeConditions = (playerStats.conditions || []);

          return (
            <div
              className="mb-3 p-2 bg-red-950/60 border border-red-700/60 rounded-lg"
              data-testid="condition-consequence-banner"
            >
              <div className="text-xs font-bold text-red-300 mb-1.5 uppercase tracking-wide">
                ⚠ Active Condition Effects
              </div>

              {autoFailAbilities.length > 0 && (
                <div className="mb-1.5">
                  <div className="text-[10px] text-gray-400 mb-1">Saving Throws:</div>
                  <div className="flex flex-wrap gap-1">
                    {autoFailAbilities.map(a => (
                      <span
                        key={a.key}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-900/80 border border-red-600/50 rounded text-xs"
                        title={`${a.full} save automatically fails due to: ${activeConditions.join(', ')}`}
                        data-testid={`auto-fail-save-${a.key}`}
                        aria-label={`${a.full} save: AUTO-FAIL`}
                      >
                        <span className="line-through text-gray-400">{a.label}</span>
                        <span className="text-red-400 font-bold text-[10px]">AUTO-FAIL</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* [Sub-plan 1Q-F-7b-ii-ui 2026-05-28]: Parallel "Ability Checks:" section */}
              {(autoFailCheckAbilities.length > 0 || disadvantageCheckList.length > 0) && (
                <div className="mb-1.5">
                  <div className="text-[10px] text-gray-400 mb-1">Ability Checks:</div>
                  <div className="flex flex-wrap gap-1">
                    {autoFailCheckAbilities.map(a => (
                      <span
                        key={a.key}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-900/80 border border-red-600/50 rounded text-xs"
                        title={`${a.full} ability check automatically fails due to: ${activeConditions.join(', ')}`}
                        data-testid={`auto-fail-check-${a.key}`}
                        aria-label={`${a.full} check: AUTO-FAIL`}
                      >
                        <span className="line-through text-gray-400">{a.label}</span>
                        <span className="text-red-400 font-bold text-[10px]">AUTO-FAIL</span>
                      </span>
                    ))}
                    {disadvantageCheckList.map(a => (
                      <span
                        key={a.key}
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-900/80 border border-amber-600/50 rounded text-xs"
                        title={`${a.full} ability check has disadvantage due to: ${activeConditions.join(', ')}`}
                        data-testid={`disadvantage-check-${a.key}`}
                        aria-label={`${a.full} check: disadvantage`}
                      >
                        <span className="text-amber-300">{a.label}</span>
                        <span className="text-amber-400 font-bold text-[10px]">DISADV</span>
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {hasAdvAtk && (
                <div
                  className="text-xs text-amber-300 mb-0.5"
                  data-testid="attacks-against-advantage-banner"
                  title={`Attacks against you have advantage due to: ${activeConditions.join(', ')}`}
                >
                  🗡 Attacks against you have advantage
                </div>
              )}

              {hasAutoCrit && (
                <div
                  className="text-xs text-orange-300"
                  data-testid="auto-crit-within-5ft-banner"
                  title={`Hits within 5 ft are critical hits due to: ${activeConditions.join(', ')}`}
                >
                  💥 Hits within 5 ft are critical hits
                </div>
              )}
            </div>
          );
        })()}

        {/* AC Bonuses */}
        <div>
          <button
            onClick={() => toggleSection('acBonuses')}
            className="flex items-center w-full text-sm font-medium text-white mb-2"
          >
            {expandedSections.acBonuses ? <ChevronDown className="mr-1 h-4 w-4" /> : <ChevronRight className="mr-1 h-4 w-4" />}
            AC Bonuses
          </button>
          {expandedSections.acBonuses && (
            <div className="space-y-2 ml-4">
              {/* [T5 — AC-bug fix 2026-05-31]: Auto-populated read-only list sourced from
                  effectRegistry. Queries the same breakdown the EffectBreakdownTooltip uses.
                  Filters to effects with non-zero value (base AC = 10 always present but
                  has no modifier). Uses PRODUCTION effectRegistry.getBreakdownForDisplay —
                  no hardcoded item-name checks (Rule 11/17). CW-authored items with acBonus
                  flow through buildItemRegistrationsFromInventory → effectRegistry → appear here
                  automatically (Rule 30c / CW-first contract). */}
              {(() => {
                const breakdown = effectRegistry.getBreakdownForDisplay(
                  'ac', 10, playerStats, { inventory, activeQuickBonuses }
                );
                const itemEffects = (breakdown.effects || []).filter(e => e.value !== 0);
                if (itemEffects.length === 0) return null;
                return (
                  /* [NS-AC-source-label-name 2026-05-31]: testid scopes the Rule-27 cap
                     assertion to THIS auto-list (the equipped item name also appears in the
                     EquipmentPanel; a page-level match was ambiguous per the queue row). */
                  <div className="mb-2" data-testid="ac-from-equipment-list">
                    <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                      From Equipment
                    </div>
                    {itemEffects.map((effect, idx) => (
                      <div key={idx} className="flex items-center justify-between p-1.5 bg-gray-700/50 rounded text-xs mb-1">
                        <span className="text-gray-200">{effect.source}</span>
                        <span className="font-mono font-semibold text-green-400">{effect.modifier}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
              {/* Manual overrides — existing editable ledger */}
              {playerStats.customACBonuses?.length > 0 && (
                <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                  Manual
                </div>
              )}
              {playerStats.customACBonuses?.map((bonus, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-gray-700 rounded">
                  <input
                    type="text"
                    value={bonus.name}
                    onChange={(e) => {
                      const newBonuses = [...(playerStats.customACBonuses || [])];
                      newBonuses[index].name = e.target.value;
                      setPlayerStats(prev => ({ ...prev, customACBonuses: newBonuses }));
                    }}
                    placeholder="Ring of Protection"
                    className="flex-1 p-1 bg-gray-600 text-white rounded text-xs"
                  />
                  <input
                    type="number"
                    value={bonus.value}
                    onChange={(e) => {
                      const newBonuses = [...(playerStats.customACBonuses || [])];
                      newBonuses[index].value = parseInt(e.target.value) || 0;
                      setPlayerStats(prev => ({ ...prev, customACBonuses: newBonuses }));
                    }}
                    className="w-12 p-1 bg-gray-600 text-white rounded text-xs text-center"
                  />
                  <button
                    onClick={() => {
                      const newBonuses = (playerStats.customACBonuses || []).filter((_, i) => i !== index);
                      setPlayerStats(prev => ({ ...prev, customACBonuses: newBonuses }));
                    }}
                    className="text-red-400 hover:text-red-300 text-xs"
                  >
                    ✕
                  </button>
                </div>
              ))}
              
              <button
                onClick={() => {
                  const newBonus = { name: '', value: 1 };
                  const newBonuses = [...(playerStats.customACBonuses || []), newBonus];
                  setPlayerStats(prev => ({ ...prev, customACBonuses: newBonuses }));
                }}
                className="w-full p-2 bg-gray-600 hover:bg-gray-500 text-white rounded text-xs transition-colors"
              >
                + Add AC Bonus
              </button>
            </div>
          )}
        </div>

        {/* Hit Bonuses */}
        <div>
          <button 
            onClick={() => toggleSection('hitBonuses')}
            className="flex items-center w-full text-sm font-medium text-white mb-2"
          >
            {expandedSections.hitBonuses ? <ChevronDown className="mr-1 h-4 w-4" /> : <ChevronRight className="mr-1 h-4 w-4" />}
            Hit Bonuses
          </button>
          {expandedSections.hitBonuses && (
            <div className="space-y-2 ml-4">
              {playerStats.customHitBonuses?.map((bonus, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-gray-700 rounded">
                  <input
                    type="text"
                    value={bonus.name}
                    onChange={(e) => {
                      const newBonuses = [...(playerStats.customHitBonuses || [])];
                      newBonuses[index].name = e.target.value;
                      setPlayerStats(prev => ({ ...prev, customHitBonuses: newBonuses }));
                    }}
                    placeholder="Weapon Focus"
                    className="flex-1 p-1 bg-gray-600 text-white rounded text-xs"
                  />
                  <input
                    type="number"
                    value={bonus.value}
                    onChange={(e) => {
                      const newBonuses = [...(playerStats.customHitBonuses || [])];
                      newBonuses[index].value = parseInt(e.target.value) || 0;
                      setPlayerStats(prev => ({ ...prev, customHitBonuses: newBonuses }));
                    }}
                    className="w-12 p-1 bg-gray-600 text-white rounded text-xs text-center"
                  />
                  <button
                    onClick={() => {
                      const newBonuses = playerStats.customHitBonuses.filter((_, i) => i !== index);
                      setPlayerStats(prev => ({ ...prev, customHitBonuses: newBonuses }));
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    ×
                  </button>
                </div>
              ))}
              
              <button
                onClick={() => {
                  const newBonus = { name: '', value: 1 };
                  const newBonuses = [...(playerStats.customHitBonuses || []), newBonus];
                  setPlayerStats(prev => ({ ...prev, customHitBonuses: newBonuses }));
                }}
                className="w-full p-2 bg-gray-600 hover:bg-gray-500 text-white rounded text-xs transition-colors"
              >
                + Add Hit Bonus
              </button>
            </div>
          )}
        </div>

        {/* Damage Bonuses */}
        <div>
          <button 
            onClick={() => toggleSection('damageBonuses')}
            className="flex items-center w-full text-sm font-medium text-white mb-2"
          >
            {expandedSections.damageBonuses ? <ChevronDown className="mr-1 h-4 w-4" /> : <ChevronRight className="mr-1 h-4 w-4" />}
            Damage Bonuses
          </button>
          {expandedSections.damageBonuses && (
            <div className="space-y-2 ml-4">
              {playerStats.customDamageBonuses?.map((bonus, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-gray-700 rounded">
                  <input
                    type="text"
                    value={bonus.name}
                    onChange={(e) => {
                      const newBonuses = [...(playerStats.customDamageBonuses || [])];
                      newBonuses[index].name = e.target.value;
                      setPlayerStats(prev => ({ ...prev, customDamageBonuses: newBonuses }));
                    }}
                    placeholder="Strength Modifier"
                    className="flex-1 p-1 bg-gray-600 text-white rounded text-xs"
                  />
                  <input
                    type="number"
                    value={bonus.value}
                    onChange={(e) => {
                      const newBonuses = [...(playerStats.customDamageBonuses || [])];
                      newBonuses[index].value = parseInt(e.target.value) || 0;
                      setPlayerStats(prev => ({ ...prev, customDamageBonuses: newBonuses }));
                    }}
                    className="w-12 p-1 bg-gray-600 text-white rounded text-xs text-center"
                  />
                  <button
                    onClick={() => {
                      const newBonuses = playerStats.customDamageBonuses.filter((_, i) => i !== index);
                      setPlayerStats(prev => ({ ...prev, customDamageBonuses: newBonuses }));
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    ×
                  </button>
                </div>
              ))}
              
              <button
                onClick={() => {
                  const newBonus = { name: '', value: 1 };
                  const newBonuses = [...(playerStats.customDamageBonuses || []), newBonus];
                  setPlayerStats(prev => ({ ...prev, customDamageBonuses: newBonuses }));
                }}
                className="w-full p-2 bg-gray-600 hover:bg-gray-500 text-white rounded text-xs transition-colors"
              >
                + Add Damage Bonus
              </button>
            </div>
          )}
        </div>

        {/* Spell Save DC Bonuses - Only show for spellcasters */}
        {(() => {
          // [v1.0]: Use contentResolver isSpellcaster() — supports custom classes
          const isSpellcaster = (
            checkIsSpellcaster(playerStats.classes?.[0]?.className || '') ||
            (playerStats.classes && playerStats.classes.some(cls =>
              checkIsSpellcaster(cls.className)
            ))
          );

          if (!isSpellcaster) return null;

          return (
        <div>
          <button 
            onClick={() => toggleSection('spellSaveBonuses')}
            className="flex items-center w-full text-sm font-medium text-white mb-2"
          >
            {expandedSections.spellSaveBonuses ? <ChevronDown className="mr-1 h-4 w-4" /> : <ChevronRight className="mr-1 h-4 w-4" />}
            Spell Save DC Bonuses
          </button>
          {expandedSections.spellSaveBonuses && (
            <div className="space-y-2 ml-4">
              {playerStats.customSpellSaveBonuses?.map((bonus, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-gray-700 rounded">
                  <input
                    type="text"
                    value={bonus.name}
                    onChange={(e) => {
                      const newBonuses = [...(playerStats.customSpellSaveBonuses || [])];
                      newBonuses[index].name = e.target.value;
                      setPlayerStats(prev => ({ ...prev, customSpellSaveBonuses: newBonuses }));
                    }}
                    placeholder="Rod of the Pact Keeper"
                    className="flex-1 p-1 bg-gray-600 text-white rounded text-xs"
                  />
                  <input
                    type="number"
                    value={bonus.value}
                    onChange={(e) => {
                      const newBonuses = [...(playerStats.customSpellSaveBonuses || [])];
                      newBonuses[index].value = parseInt(e.target.value) || 0;
                      setPlayerStats(prev => ({ ...prev, customSpellSaveBonuses: newBonuses }));
                    }}
                    className="w-12 p-1 bg-gray-600 text-white rounded text-xs text-center"
                  />
                  <button
                    onClick={() => {
                      const newBonuses = playerStats.customSpellSaveBonuses.filter((_, i) => i !== index);
                      setPlayerStats(prev => ({ ...prev, customSpellSaveBonuses: newBonuses }));
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    ×
                  </button>
                </div>
              ))}
              
              <button
                onClick={() => {
                  const newBonus = { name: '', value: 1 };
                  const newBonuses = [...(playerStats.customSpellSaveBonuses || []), newBonus];
                  setPlayerStats(prev => ({ ...prev, customSpellSaveBonuses: newBonuses }));
                }}
                className="w-full p-2 bg-gray-600 hover:bg-gray-500 text-white rounded text-xs transition-colors"
              >
                + Add Spell Save DC Bonus
              </button>
            </div>
          )}
        </div>
          );
        })()}

        {/* Spell Attack Bonuses - Only show for spellcasters */}
        {(() => {
          // [v1.0]: Use contentResolver isSpellcaster() — supports custom classes
          const isSpellcaster = (
            checkIsSpellcaster(playerStats.classes?.[0]?.className || '') ||
            (playerStats.classes && playerStats.classes.some(cls =>
              checkIsSpellcaster(cls.className)
            ))
          );

          if (!isSpellcaster) return null;

          return (
        <div>
          <button 
            onClick={() => toggleSection('spellAttackBonuses')}
            className="flex items-center w-full text-sm font-medium text-white mb-2"
          >
            {expandedSections.spellAttackBonuses ? <ChevronDown className="mr-1 h-4 w-4" /> : <ChevronRight className="mr-1 h-4 w-4" />}
            Spell Attack Bonuses
          </button>
          {expandedSections.spellAttackBonuses && (
            <div className="space-y-2 ml-4">
              {playerStats.customSpellAttackBonuses?.map((bonus, index) => (
                <div key={index} className="flex items-center gap-2 p-2 bg-gray-700 rounded">
                  <input
                    type="text"
                    value={bonus.name}
                    onChange={(e) => {
                      const newBonuses = [...(playerStats.customSpellAttackBonuses || [])];
                      newBonuses[index].name = e.target.value;
                      setPlayerStats(prev => ({ ...prev, customSpellAttackBonuses: newBonuses }));
                    }}
                    placeholder="Wand of the War Mage"
                    className="flex-1 p-1 bg-gray-600 text-white rounded text-xs"
                  />
                  <input
                    type="number"
                    value={bonus.value}
                    onChange={(e) => {
                      const newBonuses = [...(playerStats.customSpellAttackBonuses || [])];
                      newBonuses[index].value = parseInt(e.target.value) || 0;
                      setPlayerStats(prev => ({ ...prev, customSpellAttackBonuses: newBonuses }));
                    }}
                    className="w-12 p-1 bg-gray-600 text-white rounded text-xs text-center"
                  />
                  <button
                    onClick={() => {
                      const newBonuses = playerStats.customSpellAttackBonuses.filter((_, i) => i !== index);
                      setPlayerStats(prev => ({ ...prev, customSpellAttackBonuses: newBonuses }));
                    }}
                    className="text-red-400 hover:text-red-300"
                  >
                    ×
                  </button>
                </div>
              ))}
              
              <button
                onClick={() => {
                  const newBonus = { name: '', value: 1 };
                  const newBonuses = [...(playerStats.customSpellAttackBonuses || []), newBonus];
                  setPlayerStats(prev => ({ ...prev, customSpellAttackBonuses: newBonuses }));
                }}
                className="w-full p-2 bg-gray-600 hover:bg-gray-500 text-white rounded text-xs transition-colors"
              >
                + Add Spell Attack Bonus
              </button>
            </div>
          )}
        </div>
          );
        })()}

        {/* [Spec B Task 6 — 2026-05-16]: Reactions section — iterates
            registered reactionTrigger effects (Artificer Flash of Genius
            L7; future class reactions). Each row: actionName + optional
            cost badge + Invoke button. Invoke decrements the attached
            pool by `cost` + logs activity. Disabled when pool current=0.
            Reactions without an attached pool always invokable (log-only).
            Reserved-path safety (Rule 18): does NOT write to the reserved
            playerStats.reactionTriggers plural path; pool decrement
            targets classes[i][attachedToResource] (snake_case + camel
            fallback per ClassResourcesPanel:132). */}
        {(() => {
          const reactions = effectCalculator.getReactionTriggers(playerStats, inventory);
          if (!Array.isArray(reactions) || reactions.length === 0) return null;
          // [1H-G-bw-S3a]: pool decrement (class OR race) — shared by the direct
          // path and the modal-resolve path. Race pools live at
          // playerStats.raceResources[id]={current,max}, lazily materialized.
          const decrementReactionPool = (reaction) => {
            const cost = reaction.cost || 1;
            if (reaction.attachedToResource && reaction.classKey) {
              setPlayerStats(prev => {
                if (!Array.isArray(prev.classes)) return prev;
                const camelKey = reaction.attachedToResource.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
                const nextClasses = prev.classes.map(cls => {
                  if (cls.className !== reaction.classKey) return cls;
                  const key = cls[reaction.attachedToResource] != null ? reaction.attachedToResource : camelKey;
                  const pool = cls[key] || { current: 0 };
                  return { ...cls, [key]: { ...pool, current: Math.max(0, (pool.current || 0) - cost) } };
                });
                return { ...prev, classes: nextClasses };
              });
            } else if (reaction.attachedToResource && reaction.raceKey) {
              setPlayerStats(prev => {
                const stored = prev.raceResources?.[reaction.attachedToResource] || {};
                const max = stored.max ?? reaction.maxUses ?? 1;
                const curBase = stored.current ?? max;
                return {
                  ...prev,
                  raceResources: {
                    ...(prev.raceResources || {}),
                    [reaction.attachedToResource]: { ...stored, max, current: Math.max(0, curBase - cost) },
                  },
                };
              });
            }
          };
          const logReaction = (reaction, resolution) => {
            logActivity(
              `Used ${reaction.actionName} reaction`,
              'reaction',
              {
                actionName: reaction.actionName,
                attachedToResource: reaction.attachedToResource || null,
                cost: reaction.cost || null,
                ...(resolution ? { rolledTotals: resolution.rolledTotals || null, saveOutcome: resolution.saveOutcome || null, target: resolution.target || null } : {}),
              },
            );
          };
          const invokeReaction = (reaction) => {
            // [1H-G-bw-S3a]: a reaction with a rollSpec (Hellish Rebuke) opens the
            // resolution modal — decrement + log happen on Apply. The save DC is
            // computed at invoke-time (8 + abilityMod + prof) since it depends on
            // the live character, then frozen into the modal's rollSpec.
            if (reaction.rollSpec) {
              const dcAbility = reaction.rollSpec.save?.dcAbility || 'charisma';
              const mod = effectCalculator.getEffectiveModifier(playerStats, inventory, activeQuickBonuses, dcAbility);
              const profBonus = effectCalculator.getProficiencyBonus(playerStats);
              const dc = computeAbilitySaveDc(mod, profBonus);
              const resolvedRollSpec = {
                ...reaction.rollSpec,
                save: reaction.rollSpec.save ? { ...reaction.rollSpec.save, dc } : null,
              };
              setReactionModal({ reaction, rollSpec: resolvedRollSpec });
              return;
            }
            decrementReactionPool(reaction);
            logReaction(reaction, null);
          };
          const handleReactionResolve = (resolution) => {
            const reaction = reactionModal?.reaction;
            if (reaction) {
              decrementReactionPool(reaction);
              logReaction(reaction, resolution);
            }
            setReactionModal(null);
          };
          return (
            <>
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-amber-300 mb-2">Reactions</h3>
              <div className="space-y-2">
                {reactions.map((reaction, idx) => {
                  const hasPool = reaction.attachedToResource != null;
                  const cannotInvoke = hasPool && (reaction.currentUses == null || reaction.currentUses <= 0);
                  return (
                    <div
                      key={`${reaction.actionName}:${idx}`}
                      className="flex items-start justify-between gap-2 p-2 bg-gray-800/60 border border-gray-700 rounded"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-amber-200">
                          {reaction.actionName}
                          {hasPool && (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 bg-black/40 text-amber-300 rounded font-mono">
                              {reaction.currentUses}/{reaction.maxUses ?? '?'}
                            </span>
                          )}
                        </div>
                        {reaction.description && (
                          <div className="text-[11px] text-gray-400 mt-0.5">
                            {reaction.description}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => invokeReaction(reaction)}
                        disabled={cannotInvoke}
                        className="px-2 py-1 text-xs font-semibold bg-purple-700 hover:bg-purple-600 disabled:bg-gray-700 disabled:text-gray-500 text-white rounded disabled:cursor-not-allowed"
                        aria-label={`Invoke ${reaction.actionName}`}
                      >
                        Invoke
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
            {reactionModal && (
              <UsableAbilityModal
                isOpen={!!reactionModal}
                onClose={() => setReactionModal(null)}
                title={reactionModal.reaction.actionName}
                rollSpec={reactionModal.rollSpec}
                costSpend={{ type: 'raceResource', id: reactionModal.reaction.attachedToResource }}
                rollProtocol="d20"
                maxPoolHp={null}
                onResolve={handleReactionResolve}
              />
            )}
            </>
          );
        })()}

        {/* Encumbrance & Limits */}
        <div>
          {(() => {
            // [INTEGRATION]: Use EffectCalculator for carry capacity (includes STR bonuses from items/feats)
            const carryCapacity = effectCalculator.calculateCarryCapacity(
              playerStats,
              inventory,
              activeQuickBonuses
            );
            return (
              <div className="text-sm text-gray-300 mb-2">
                <div
                  title={`PHB p.176 variant encumbrance:\n• Encumbered at ${carryCapacity.encumberedAt} lb (STR × 5) — speed −10\n• Heavily encumbered at ${carryCapacity.heavilyEncumberedAt} lb (STR × 10) — speed −20 + disadvantage on STR/DEX/CON checks\n• Max capacity at ${carryCapacity.capacity} lb (STR × ${carryCapacity.powerfulBuild ? 30 : 15})`}
                  className="cursor-help inline-flex items-center gap-1"
                >
                  Max Carry Capacity: {carryCapacity.capacity} lbs
                  <span className="text-xs text-gray-500">ⓘ</span>
                </div>
                {carryCapacity.powerfulBuild && (
                  <span className="text-xs text-green-400 ml-2">
                    (Powerful Build: ×2)
                  </span>
                )}
                <div className="text-[11px] text-gray-500 mt-0.5">
                  Encumbered at {carryCapacity.encumberedAt} lb · Heavily encumbered at {carryCapacity.heavilyEncumberedAt} lb
                </div>
              </div>
            );
          })()}
          <div className="w-full bg-gray-700 rounded-full h-4 mt-1">
            <div
              className={`h-full rounded-full transition-all ${
                encumbranceLevel === 'light' ? 'bg-green-600' :
                encumbranceLevel === 'medium' ? 'bg-yellow-600' :
                encumbranceLevel === 'heavy' ? 'bg-orange-600' :
                encumbranceLevel === 'encumbered' ? 'bg-red-600' :
                'bg-gray-600'
              }`}
              style={{ width: `${Math.min((totalWeight / effectCalculator.calculateCarryCapacity(playerStats, inventory, activeQuickBonuses).capacity) * 100, 100)}%` }}
            />
          </div>
          <div className="text-xs text-gray-400 mt-1">
            {totalWeight.toFixed(1)} / {effectCalculator.calculateCarryCapacity(playerStats, inventory, activeQuickBonuses).capacity} lbs ({encumbranceLevel})
          </div>
        </div>
        
        <label className="flex items-center text-sm text-gray-300">
          <input 
            type="checkbox"
            checked={playerStats.useWeightLimit}
            onChange={(e) => setPlayerStats(prev => ({
              ...prev,
              useWeightLimit: e.target.checked
            }))}
            className="mr-2"
          />
          Use Weight Limits
        </label>
        
      </div>
    </div>
  );
};

// [PHASE 4.5]: Wrapped with React.memo to prevent unnecessary re-renders
// Compares totalWeight, encumbranceLevel, getAvailableSpells, resetDailySpells props
// Will re-render if any prop changes or context changes
export default React.memo(StatsPanel);